import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { attention } from '@platform/client';
import {
  attentionQueueSchema,
  BUCKET_ORDER,
  caughtUp,
  emptySummary,
} from '@platform/schema/attention';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PAGE, Pager } from '../library/Pager';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import {
  BUCKET_LABEL,
  nextAction,
  PRIORITY_COLOUR,
  PRIORITY_LABEL,
} from './next-action';

import type {
  AttentionBucket,
  AttentionItem,
  LastImport,
} from '@platform/schema/attention';

/**
 * What needs an admin's attention, in one prioritized queue — ADMIN-15.
 *
 * Detect, prioritize, explain, act, clear. Every row says what happened in a sentence and
 * carries one action; taking it changes the row's bucket and the row leaves on the next
 * fetch. Deliberately not an analytics dashboard — nothing here is a chart, and nothing
 * counts what is already finished. `docs/decisions/admin-action-center.md`.
 */
interface Filter {
  bucket?: AttentionBucket | undefined;
  jobId?: string | undefined;
}

export function ActionCenter() {
  const [filter, setFilter] = useState<Filter>({});
  const [offset, setOffset] = useState(0);
  const { bucket, jobId } = filter;

  const queue = useQuery({
    queryKey: ['attention', bucket, jobId, offset],
    // Parsed, not cast. The generator drops a discriminated union's `const` down to
    // `kind: string` and loses every branded id with it, so the SDK's own type cannot
    // tell a LESSON target from a JOB one — the contract can, and it is right here.
    queryFn: async () => {
      const response = await attention({
        query: {
          limit: PAGE,
          offset,
          ...(bucket === undefined ? {} : { bucket }),
          ...(jobId === undefined ? {} : { jobId }),
        },
        throwOnError: true,
      });

      return attentionQueueSchema.parse(response.data);
    },
    // A run finishing is the event this screen exists to notice, and nobody reloads a
    // page to find out. Slower than the job screen's poll on purpose: this is the
    // background tab, not the one being watched.
    refetchInterval: 30_000,
  });

  const summary = queue.data?.summary ?? emptySummary();
  const items = queue.data?.items ?? [];
  const total = queue.data?.total ?? 0;
  const lastImport = queue.data?.lastImport;

  // A spread already treats an absent key as unchanged and an explicit `undefined` as a
  // clear, which is what two `useState`s and an `in` test were doing by hand.
  const refine = (change: Filter): void => {
    setFilter({ ...filter, ...change });
    setOffset(0);
  };

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="Action Center"
        subtitle="Everything waiting on a decision, most urgent first."
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
        role="group"
        aria-label="Filter the queue"
      >
        {BUCKET_ORDER.map((one) => (
          <SummaryCard
            key={one}
            bucket={one}
            count={summary[one]}
            selected={bucket === one}
            onPick={() => {
              refine({ bucket: bucket === one ? undefined : one });
            }}
          />
        ))}
      </Stack>

      {lastImport !== undefined && jobId === undefined && (
        <ImportBanner
          lastImport={lastImport}
          onOpen={() => {
            refine({ jobId: lastImport.jobId, bucket: undefined });
          }}
        />
      )}

      {jobId !== undefined && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">Showing one import only.</Typography>
          <Button
            size="small"
            onClick={() => {
              refine({ jobId: undefined });
            }}
          >
            Show everything
          </Button>
        </Stack>
      )}

      {queue.isPending && <LinearProgress />}

      {!queue.isPending && caughtUp(summary) && (
        <Alert severity="success">
          <AlertTitle>You&rsquo;re caught up.</AlertTitle>
          No lessons currently need review or attention.
        </Alert>
      )}

      {!queue.isPending && !caughtUp(summary) && items.length === 0 && (
        <Alert severity="info">
          Nothing in this filter. Pick another card, or show everything.
        </Alert>
      )}

      {items.map((item) => (
        <QueueRow key={`${item.target.kind}:${item.target.id}`} item={item} />
      ))}

      <Pager offset={offset} total={total} onOffset={setOffset} />
    </Stack>
  );
}

/**
 * One count, and the filter it turns on.
 *
 * A button rather than a card with a link inside it: the count and the filter are the
 * same thing said twice, and a reader who wants to know what the 4 is made of clicks the
 * 4. Clicking the selected one clears it, so there is no separate "All" to keep in sync
 * with the six.
 *
 * `color="inherit"` when selected, deliberately. Selection is a state and the row's
 * action is an act, and filling both in primary leaves the screen with no primary — the
 * thing `screen-design` calls a defect rather than a preference.
 */
function SummaryCard({
  bucket,
  count,
  selected,
  onPick,
}: {
  bucket: AttentionBucket;
  count: number;
  selected: boolean;
  onPick: () => void;
}) {
  return (
    <Button
      variant={selected ? 'contained' : 'outlined'}
      color="inherit"
      onClick={onPick}
      aria-pressed={selected}
      sx={{ flexDirection: 'column', minWidth: 140, py: 1 }}
    >
      <Typography variant="h5" component="span">
        {String(count)}
      </Typography>
      <Typography variant="caption" component="span">
        {BUCKET_LABEL[bucket]}
      </Typography>
    </Button>
  );
}

/**
 * What last night's import did — the brief's "open Admin the next morning" case.
 *
 * Counts only, and one way in. The four actions it sketches (`Review New Lessons`,
 * `Resolve 9 Warnings`, `Retry 3 Failed`, `Open Import`) are three filters and a
 * mutation; the filters are the cards above, already, and retry is a write this screen
 * does not have — `docs/decisions/deferred.md`.
 */
function ImportBanner({
  lastImport,
  onOpen,
}: {
  lastImport: LastImport;
  onOpen: () => void;
}) {
  const failed = lastImport.rowsTotal - lastImport.rowsSucceeded;

  return (
    <Alert
      severity={failed > 0 ? 'warning' : 'info'}
      action={
        <Button size="small" onClick={onOpen}>
          Open import
        </Button>
      }
    >
      <AlertTitle>The last import is finished.</AlertTitle>
      {String(lastImport.lessonsCreated)} lessons created ·{' '}
      {String(lastImport.rowsSucceeded)} of {String(lastImport.rowsTotal)} rows
      succeeded
      {failed > 0 && ` · ${String(failed)} failed`}
    </Alert>
  );
}

/**
 * One thing waiting, and the one thing to do about it.
 *
 * Title, where it lives, what happened, what to do — in that order, because that is the
 * order a person decides in: is this mine, where is it, is it bad, and then act.
 */
function QueueRow({ item }: { item: AttentionItem }) {
  const action = nextAction(item);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle1" component="h2">
              {item.title}
            </Typography>
            <Chip
              size="small"
              label={PRIORITY_LABEL[item.priority]}
              color={PRIORITY_COLOUR[item.priority]}
            />
            <Chip size="small" label={BUCKET_LABEL[item.bucket]} />
          </Stack>
          {item.context !== '' && (
            <Typography variant="body2" color="text.secondary">
              {item.context}
            </Typography>
          )}
          <Typography variant="body2">{item.reason}</Typography>
        </Stack>

        <Button
          component={Link}
          to={action.to}
          variant="contained"
          size="small"
          sx={{ flexShrink: 0 }}
        >
          {action.label}
        </Button>
      </Stack>
    </Paper>
  );
}
